import { useId, useMemo, useState } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import { usePatchCart } from "@/customer/cart/hooks";
import type { Cart } from "@/customer/cart/types";

const WINDOW_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 2, label: "2-hour window" },
  { value: 3, label: "3-hour window" },
  { value: 4, label: "4-hour window" }
];

// Delivery date + window. The v1 picker is the native HTML date input;
// a blackout-aware shadcn Calendar is a follow-up. We enforce a
// reasonable client-side min (today + 1) so the customer can't pick a
// past date; the server re-checks notice_required_hours + holiday
// blackouts at finalize.
export function DeliveryDateSection({
  cart,
  onAdvance
}: {
  cart: Cart;
  onAdvance(): void;
}): JSX.Element {
  const ids = { start: useId(), end: useId(), window: useId() };
  const today = useMemo(() => isoDate(new Date()), []);
  const minStart = useMemo(() => addDays(today, 1), [today]);

  const [start, setStart] = useState(cart.payload.delivery_date_start ?? "");
  const [end, setEnd] = useState(cart.payload.delivery_date_end ?? "");
  const [windowHours, setWindowHours] = useState(
    String(cart.payload.delivery_window_hours ?? 2)
  );

  const patch = usePatchCart();

  const validationError = useMemo(() => {
    if (!start || !end) return null;
    if (end < start) return "Pickup date must be on or after delivery date.";
    if (start < minStart) return "Delivery must be at least one day in the future.";
    return null;
  }, [start, end, minStart]);

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!start || !end || validationError) return;
    patch.mutate(
      {
        delivery_date_start: start,
        delivery_date_end: end,
        delivery_window_hours: Number(windowHours)
      },
      { onSuccess: () => onAdvance() }
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-zinc-600">
        We deliver Monday–Saturday. Pickups never happen on Sundays or state
        holidays — we&rsquo;ll confirm the exact pickup window the day before.
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label htmlFor={ids.start} className="mb-1 block text-sm">
            Delivery date
          </Label>
          <Input
            id={ids.start}
            type="date"
            min={minStart}
            value={start}
            onChange={(e) => setStart(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor={ids.end} className="mb-1 block text-sm">
            Pickup date
          </Label>
          <Input
            id={ids.end}
            type="date"
            min={start || minStart}
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            required
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor={ids.window} className="mb-1 block text-sm">
            Delivery time window
          </Label>
          <Select
            id={ids.window}
            value={windowHours}
            onChange={(e) => setWindowHours(e.target.value)}
          >
            {WINDOW_OPTIONS.map((opt) => (
              <option key={opt.value} value={String(opt.value)}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {validationError ? (
        <Alert variant="destructive" role="alert">
          {validationError}
        </Alert>
      ) : null}

      <div className="flex justify-end">
        <Button type="submit" disabled={!start || !end || !!validationError || patch.isPending}>
          {patch.isPending ? "Saving…" : "Save and continue"}
        </Button>
      </div>

      {patch.isError ? (
        <Alert variant="destructive">
          We couldn&rsquo;t save your dates — try again or call (303) 435-4328.
        </Alert>
      ) : null}
    </form>
  );
}

function isoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return isoDate(d);
}
